import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SaveIcon from "@mui/icons-material/Save";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import SyncIcon from "@mui/icons-material/Sync";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PageBanner from "../../components/PageBanner";
import PageContent from "../../components/PageContent";
import { AppToast } from "../../components/AppFeedback";
import { useUserContext } from "../../context/UserContext";
import { fetchClientDropdown } from "../../services/clientService";
import { fetchProjectsDropdownByClient } from "../../services/projectService";
import ReportAdvancedFilters, {
  REPORTS_WITHOUT_CREATED_AT_FILTERS,
  sanitizeReportConfig,
} from "./ReportAdvancedFilters";
import {
  deleteSavedReport,
  downloadSavedReportCsv,
  downloadSavedReportPdf,
  exportReportCsv,
  exportReportPdf,
  fetchReportConfig,
  fetchSavedReports,
  previewReport,
  saveReport,
  syncSavedReport,
} from "../../services/reportService";
import { formatDate, formatDateTime } from "../../utils/dateFormat";

const REPORT_TYPES = [
  {
    id: "client",
    label: "Client",
    description: "Client list with project associations",
    adminManagerOnly: true,
  },
  {
    id: "project",
    label: "Client Project",
    description: "Projects with schedules and subject counts",
  },
  {
    id: "patient",
    label: "Subject",
    description: "Subjects with enrollment and survey completion",
  },
  {
    id: "users_roles",
    label: "User and Roles",
    description: "Users with assigned roles",
    adminOnly: true,
  },
  {
    id: "activities",
    label: "Activities",
    description: "API activity logs from daily log files (admin only)",
    adminOnly: true,
  },
  {
    id: "questionnaire",
    label: "Questionnaires",
    description: "Questionnaire catalog linked to your projects",
  },
];

const REPORT_TYPE_LABELS = Object.fromEntries(REPORT_TYPES.map((item) => [item.id, item.label]));

const alternatingRowGridSx = {
  "& .row-even": {
    backgroundColor: "#ffffff",
  },
  "& .row-odd": {
    backgroundColor: "#f5f5f5",
  },
};

const previewGridSx = {
  border: "none",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f5f5f5",
  },
  ...alternatingRowGridSx,
};

const FILTER_KEYS = [
  "search",
  "status",
  "cid",
  "client_id",
  "project_id",
  "patient_status",
  "role",
  "username",
  "email",
  "activity_date",
  "request_method",
  "request_path",
  "start_date_from",
  "start_date_to",
  "end_date_from",
  "end_date_to",
  "enrollment_date_from",
  "enrollment_date_to",
  "created_at_from",
  "created_at_to",
  "sort_by",
  "sort_order",
];

const EMPTY_FILTERS = Object.fromEntries(FILTER_KEYS.map((key) => [key, ""]));

const REPORT_DATE_FIELDS = new Set([
  "created_at",
  "project_start_date",
  "project_end_date",
  "enrollment_date",
]);

const formatReportColumnValue = (field, value) => {
  if (field === "timestamp") return formatDateTime(value);
  if (REPORT_DATE_FIELDS.has(field)) return formatDate(value);
  return value;
};

const toErrorMessage = (err, fallback) => {
  try {
    const parsed = JSON.parse(err.message);
    if (parsed?.detail) return String(parsed.detail);
  } catch {
    // not JSON
  }
  return err?.message || fallback;
};

const buildPayload = (reportType, filters, reportConfig) => {
  const payload = {};
  const skipCreatedAt = REPORTS_WITHOUT_CREATED_AT_FILTERS.has(reportType);

  for (const field of reportConfig?.filters || []) {
    if (skipCreatedAt && (field.key === "created_at_from" || field.key === "created_at_to")) {
      continue;
    }
    const rawValue = filters[field.key];
    if (rawValue === "" || rawValue == null) continue;

    if (field.key === "project_id") {
      payload.project_id = Number(rawValue);
      continue;
    }

    const value = typeof rawValue === "string" ? rawValue.trim() : rawValue;
    if (value !== "") {
      payload[field.key] = value;
    }
  }

  if (filters.sort_by) payload.sort_by = filters.sort_by;
  if (filters.sort_order) payload.sort_order = filters.sort_order;

  return {
    report_type: reportType,
    filters: payload,
  };
};

const ReportsPage = () => {
  const { roles = [] } = useUserContext();
  const normalizedRoles = roles.map((role) => String(role || "").toUpperCase());
  const isAdmin = normalizedRoles.includes("ADMIN");
  const isManager = normalizedRoles.includes("MANAGER");

  const [reportType, setReportType] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loadingAction, setLoadingAction] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewRowCount, setPreviewRowCount] = useState(0);
  const [previewColumns, setPreviewColumns] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const previewDebounceRef = useRef(null);

  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");

  const [savedOpen, setSavedOpen] = useState(false);
  const [savedRows, setSavedRows] = useState([]);
  const [savedRowCount, setSavedRowCount] = useState(0);
  const [savedPagination, setSavedPagination] = useState({ page: 0, pageSize: 10 });
  const [savedLoading, setSavedLoading] = useState(false);
  const [syncingUid, setSyncingUid] = useState("");

  const [reportConfigList, setReportConfigList] = useState([]);
  const [sortOrderOptions, setSortOrderOptions] = useState([]);
  const [configLoading, setConfigLoading] = useState(true);

  const reportConfigMap = useMemo(
    () => Object.fromEntries((reportConfigList || []).map((item) => [item.report_type, item])),
    [reportConfigList]
  );

  const getDefaultFilters = (typeId) => {
    const config = reportConfigMap[typeId];
    const base = {
      ...EMPTY_FILTERS,
      sort_by: config?.default_sort_by || "",
      sort_order: config?.default_sort_order || "asc",
    };
    if (REPORTS_WITHOUT_CREATED_AT_FILTERS.has(typeId)) {
      base.created_at_from = "";
      base.created_at_to = "";
    }
    return base;
  };

  const visibleReportTypes = useMemo(() => {
    if (reportConfigList.length) {
      return reportConfigList.map((item) => ({
        id: item.report_type,
        label: item.label,
        description: item.description,
      }));
    }
    return REPORT_TYPES.filter((item) => {
      if (item.adminOnly) return isAdmin;
      if (item.adminManagerOnly) return isAdmin || isManager;
      return true;
    });
  }, [reportConfigList, isAdmin, isManager]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setConfigLoading(true);
      try {
        const data = await fetchReportConfig();
        if (!cancelled) {
          setReportConfigList(sanitizeReportConfig(data.reports || []));
          setSortOrderOptions(data.sort_order_options || []);
        }
      } catch (err) {
        if (!cancelled) {
          setReportConfigList([]);
          setSortOrderOptions([]);
          setSnackbar({
            open: true,
            message: toErrorMessage(err, "Failed to load report field configuration"),
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) setConfigLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await fetchClientDropdown();
        if (!cancelled) setClients(items || []);
      } catch {
        if (!cancelled) setClients([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const cid = filters.client_id || filters.cid;
    if (!cid) {
      setProjects([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const items = await fetchProjectsDropdownByClient(cid);
        if (!cancelled) setProjects(items || []);
      } catch {
        if (!cancelled) setProjects([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filters.client_id, filters.cid]);

  const loadSavedReports = async (pagination = savedPagination) => {
    setSavedLoading(true);
    try {
      const data = await fetchSavedReports({
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
      setSavedRows(
        (data.rows || []).map((row) => ({
          ...row,
          id: row.uid,
          report_type_label: REPORT_TYPE_LABELS[row.report_type] || row.report_type,
        }))
      );
      setSavedRowCount(data.row_count || 0);
    } catch (err) {
      setSnackbar({
        open: true,
        message: toErrorMessage(err, "Failed to load saved reports"),
        severity: "error",
      });
    } finally {
      setSavedLoading(false);
    }
  };

  useEffect(() => {
    if (savedOpen) {
      loadSavedReports();
    }
  }, [savedOpen, savedPagination.page, savedPagination.pageSize]);

  const handleSelectReportType = (typeId) => {
    setReportType(typeId);
    setFilters(getDefaultFilters(typeId));
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "client_id" || field === "cid") {
        next.project_id = "";
      }
      return next;
    });
  };

  useEffect(() => {
    if (previewDebounceRef.current) {
      clearTimeout(previewDebounceRef.current);
    }

    if (!reportType) {
      setPreviewColumns([]);
      setPreviewRows([]);
      setPreviewRowCount(0);
      setPreviewError("");
      setPreviewLoading(false);
      return undefined;
    }

    setPreviewLoading(true);
    setPreviewError("");

    previewDebounceRef.current = setTimeout(async () => {
      try {
        const data = await previewReport(buildPayload(reportType, filters, reportConfigMap[reportType]));
        setPreviewColumns(
          (data.columns || []).map((col) => ({
            field: col.field,
            headerName: col.headerName,
            flex: 1,
            minWidth: 110,
            ...(REPORT_DATE_FIELDS.has(col.field) || col.field === "timestamp"
              ? { valueFormatter: (value) => formatReportColumnValue(col.field, value) }
              : {}),
          }))
        );
        setPreviewRows(
          (data.rows || []).map((row, index) => ({
            id: row.id || row.pid || row.qid || row.client_id || row.project_id || row.username || index,
            ...row,
          }))
        );
        setPreviewRowCount(data.row_count || 0);
      } catch (err) {
        setPreviewColumns([]);
        setPreviewRows([]);
        setPreviewRowCount(0);
        setPreviewError(toErrorMessage(err, "Failed to load report preview"));
      } finally {
        setPreviewLoading(false);
      }
    }, 450);

    return () => {
      if (previewDebounceRef.current) {
        clearTimeout(previewDebounceRef.current);
      }
    };
  }, [reportType, filters]);

  const runWithPayload = async (action, fn) => {
    if (!reportType) {
      setSnackbar({ open: true, message: "Select a report type first", severity: "warning" });
      return;
    }
    setLoadingAction(action);
    try {
      await fn(buildPayload(reportType, filters, reportConfigMap[reportType]));
    } catch (err) {
      setSnackbar({
        open: true,
        message: toErrorMessage(err, `Failed to ${action}`),
        severity: "error",
      });
    } finally {
      setLoadingAction("");
    }
  };

  const handleSave = async () => {
    if (!saveName.trim()) {
      setSnackbar({ open: true, message: "Report name is required", severity: "warning" });
      return;
    }
    setLoadingAction("save");
    try {
      await saveReport({
        ...buildPayload(reportType, filters, reportConfigMap[reportType]),
        name: saveName.trim(),
      });
      setSaveOpen(false);
      setSaveName("");
      setSnackbar({ open: true, message: "Report saved successfully", severity: "success" });
    } catch (err) {
      setSnackbar({
        open: true,
        message: toErrorMessage(err, "Failed to save report"),
        severity: "error",
      });
    } finally {
      setLoadingAction("");
    }
  };

  const savedColumns = [
    { field: "name", headerName: "Name", flex: 1.2, minWidth: 160 },
    { field: "report_type_label", headerName: "Type", flex: 1, minWidth: 140 },
    { field: "row_count", headerName: "Rows", width: 90 },
    {
      field: "updated_at",
      headerName: "Last Synced",
      flex: 1,
      minWidth: 170,
      valueFormatter: (value) => (value ? new Date(value).toLocaleString() : ""),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 220,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            title="Sync to latest"
            disabled={syncingUid === params.row.uid}
            onClick={async () => {
              setSyncingUid(params.row.uid);
              try {
                await syncSavedReport(params.row.uid);
                await loadSavedReports();
                setSnackbar({ open: true, message: "Report synced to latest data", severity: "success" });
              } catch (err) {
                setSnackbar({
                  open: true,
                  message: toErrorMessage(err, "Failed to sync report"),
                  severity: "error",
                });
              } finally {
                setSyncingUid("");
              }
            }}
          >
            <SyncIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            title="Download CSV"
            onClick={async () => {
              try {
                await downloadSavedReportCsv(params.row.uid);
              } catch (err) {
                setSnackbar({
                  open: true,
                  message: toErrorMessage(err, "Failed to download CSV"),
                  severity: "error",
                });
              }
            }}
          >
            <FileDownloadIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            title="Download PDF"
            onClick={async () => {
              try {
                await downloadSavedReportPdf(params.row.uid);
              } catch (err) {
                setSnackbar({
                  open: true,
                  message: toErrorMessage(err, "Failed to download PDF"),
                  severity: "error",
                });
              }
            }}
          >
            <PictureAsPdfIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            title="Delete"
            onClick={async () => {
              try {
                await deleteSavedReport(params.row.uid);
                await loadSavedReports();
                setSnackbar({ open: true, message: "Saved report deleted", severity: "success" });
              } catch (err) {
                setSnackbar({
                  open: true,
                  message: toErrorMessage(err, "Failed to delete report"),
                  severity: "error",
                });
              }
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const selectedReportMeta = visibleReportTypes.find((item) => item.id === reportType);

  const actionsDisabled = !reportType || Boolean(loadingAction);

  return (
    <Box sx={{ bgcolor: "#dde7ecff", minHeight: "100vh", pb: { xs: 8, md: 10 } }}>
      <PageBanner title="Reports" />

      <PageContent width={{ xs: "100%", sm: "98%", md: "95%", lg: "90%" }}>
          <Box
            sx={{
              borderRadius: 1,
              p: { xs: 2, md: 2.5 },
              bgcolor: "#ffffff",
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              gap: 2.5,
              alignItems: "stretch",
              minHeight: { xs: "auto", lg: "calc(100vh - 280px)" },
            }}
          >
            <Box
              sx={{
                flex: { xs: "1 1 auto", lg: "1 1 0" },
                minWidth: 0,
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
                Report setup
              </Typography>
              <TextField
                fullWidth
                size="small"
                select
                label="Report Type"
                value={reportType}
                onChange={(event) => handleSelectReportType(event.target.value)}
                helperText={selectedReportMeta?.description || "Select a report type"}
                InputLabelProps={{ shrink: true }}
                disabled={configLoading}
                sx={{ mb: reportType ? 2.5 : 0 }}
              >
                <MenuItem value="">
                  <em>Select a report</em>
                </MenuItem>
                {visibleReportTypes.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>

              {reportType ? (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Filters
                    </Typography>
                    <Button
                      variant="text"
                      onClick={() => setFilters(getDefaultFilters(reportType))}
                      sx={{ textTransform: "none", px: 0, minWidth: 0, fontSize: "0.8rem" }}
                    >
                      Reset
                    </Button>
                  </Box>
                  <ReportAdvancedFilters
                    reportConfig={reportConfigMap[reportType]}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    clients={clients}
                    projects={projects}
                    sortOrderOptions={sortOrderOptions}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Select a report type to configure filters and sorting.
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                flex: { xs: "1 1 auto", lg: "5 5 0" },
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                borderLeft: { xs: "none", lg: "1px solid" },
                borderTop: { xs: "1px solid", lg: "none" },
                borderColor: "divider",
                pl: { xs: 0, lg: 2.5 },
                pt: { xs: 2.5, lg: 0 },
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Report preview
                </Typography>
                {reportType && !previewLoading && !previewError && (
                  <Typography variant="body2" color="text.secondary">
                    {previewRowCount} row{previewRowCount === 1 ? "" : "s"}
                  </Typography>
                )}
              </Box>

              {!reportType ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "text.secondary",
                    minHeight: 480,
                  }}
                >
                  <Typography variant="body2">Select a report type to see live preview.</Typography>
                </Box>
              ) : previewLoading ? (
                <Box sx={{ flex: 1, minHeight: 480, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CircularProgress size={36} />
                </Box>
              ) : previewError ? (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 480,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "error.main",
                    px: 2,
                  }}
                >
                  <Typography variant="body2">{previewError}</Typography>
                </Box>
              ) : (
                <Box sx={{ flex: 1, minHeight: 480, width: "100%" }}>
                  <DataGrid
                    rows={previewRows}
                    columns={previewColumns}
                    disableRowSelectionOnClick
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    density="compact"
                    getRowClassName={(params) =>
                      params.indexRelativeToCurrentPage % 2 === 0 ? "row-even" : "row-odd"
                    }
                    sx={{ ...previewGridSx, height: "100%", minHeight: 480 }}
                  />
                </Box>
              )}
            </Box>
          </Box>
      </PageContent>

      <Paper
        elevation={4}
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          px: { xs: 2, md: 3 },
          py: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "flex-end",
          alignItems: "center",
          zIndex: 1200,
          bgcolor: "background.paper",
        }}
      >
        <Button
          variant="outlined"
          startIcon={<FolderOpenIcon />}
          onClick={() => setSavedOpen(true)}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Saved Reports
        </Button>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          disabled={actionsDisabled}
          onClick={() => runWithPayload("export csv", exportReportCsv)}
          sx={{ textTransform: "none" }}
        >
          {loadingAction === "export csv" ? "Exporting..." : "Export CSV"}
        </Button>
        <Button
          variant="outlined"
          startIcon={<PictureAsPdfIcon />}
          disabled={actionsDisabled}
          onClick={() => runWithPayload("export pdf", exportReportPdf)}
          sx={{ textTransform: "none" }}
        >
          {loadingAction === "export pdf" ? "Exporting..." : "Export PDF"}
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={actionsDisabled}
          onClick={() => {
            setSaveName(`${REPORT_TYPE_LABELS[reportType] || "Report"} ${new Date().toLocaleDateString()}`);
            setSaveOpen(true);
          }}
          sx={{ textTransform: "none" }}
        >
          Save Report
        </Button>
      </Paper>

      <Dialog
        open={saveOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          setSaveOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { minWidth: { xs: "92vw", sm: 480 } } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>Save Report</DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Save the current report with its filters and sort settings. You can sync or download it later from Saved Reports.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Report name"
            placeholder="Enter a name for this report"
            value={saveName}
            onChange={(event) => setSaveName(event.target.value)}
            helperText={
              reportType
                ? `Report type: ${REPORT_TYPE_LABELS[reportType] || reportConfigMap[reportType]?.label || reportType}`
                : ""
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setSaveOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loadingAction === "save"}
            sx={{ textTransform: "none" }}
          >
            {loadingAction === "save" ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={savedOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          setSavedOpen(false);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Saved Reports
          <IconButton
            onClick={() => setSavedOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ height: 420, width: "100%" }}>
            <DataGrid
              rows={savedRows}
              columns={savedColumns}
              loading={savedLoading}
              rowCount={savedRowCount}
              paginationMode="server"
              paginationModel={savedPagination}
              onPaginationModelChange={setSavedPagination}
              pageSizeOptions={[10, 20]}
              disableRowSelectionOnClick
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? "row-even" : "row-odd"
              }
              sx={{ ...alternatingRowGridSx, height: "100%" }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <AppToast
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default ReportsPage;
